import React from 'react'
import styled from '@emotion/styled'
import {SizesType} from 'types/Sizes'
import {ColorPath} from 'types/Theme'

import {cssTheme} from '../../../themes'
import {getCssColorByPath} from '../../../utils/colors'

type HorizontalProgressSize = Extract<SizesType, 'xs' | 'sm'>
interface Props {
  /** Pass through classname to allow styles overrides */
  className?: string
  /** The fill percentage of the bar */
  value: number
  /** Specify the color path from the theme's colors file to explicitly set the bar's color */
  colorPath?: ColorPath
  /** The height of the bar */
  size?: HorizontalProgressSize
  /** Whether the bar should have rounded corners */
  rounded?: boolean
}

const sizeMap: Record<HorizontalProgressSize, number> = {
  xs: 2,
  sm: 4
}

export const HorizontalProgressBarElement = styled.div<{
  size: HorizontalProgressSize
  rounded?: boolean
}>`
  width: 100%;
  display: block;
  height: ${({size}) => sizeMap[size]}px;
  border-radius: ${({rounded}) =>
    rounded ? cssTheme.layout.radius.rounded : 0};
  padding: 0;
  background-color: ${cssTheme.colors.scoreBar.background};
`

export const HorizontalProgressBarValue = styled.div<{
  colorValue?: string
  rounded?: boolean
  value: number
}>`
  background-color: ${({colorValue, value}) =>
    colorValue ? colorValue : barColor({value})};
  border-radius: ${({rounded}) =>
    rounded ? cssTheme.layout.radius.rounded : '0px'};
  z-index: 1;
  height: 100%;
  max-width: 100%;
  width: ${({value}) => `${value}%`};
  transition: width 0.6s ease;
`

const barColor = ({value}: {value: number}) => {
  if (value >= 70) return cssTheme.colors.scoreBar.indicator.success
  return cssTheme.colors.scoreBar.indicator.neutral
}

export const HorizontalProgressBar = ({
  value,
  size = 'sm',
  colorPath,
  className,
  rounded = true
}: Props) => {
  const colorValue = colorPath && getCssColorByPath(colorPath)

  return (
    <HorizontalProgressBarElement
      size={size}
      className={className}
      rounded={rounded}
    >
      <HorizontalProgressBarValue
        rounded={rounded}
        value={value}
        colorValue={colorValue}
      />
    </HorizontalProgressBarElement>
  )
}
